import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import DepartmentBanner from '../common/DepartmentBanner';
import styles from './Home.module.css';
import { FaBook, FaChalkboardTeacher, FaUserTie, FaFileAlt, FaCalendarAlt, FaClock, FaArrowRight, FaGraduationCap, FaCalendarCheck, FaUniversity } from 'react-icons/fa';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import DepartmentService from '../../services/Department/DepartmentService'; // Adjust the import path as necessary

const Home = () => {
  document.title = "Preview website - " + "Home";
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const deptId = queryParams.get('deptId') || location.pathname.split('/')[1];
  const [selectedCard, setSelectedCard] = useState(null);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  
  const [departmentDetails, setDepartmentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isServicesLoading, setIsServicesLoading] = useState(true);
  const [navItems, setNavItems] = useState([]);
  
  const defaultNavItems = [
    { 
      name: "Degrees & Certificates", 
      icon: <FaGraduationCap size={40} />, 
      link: `DegreeAndCertificate`,
      description: "Explore available degrees and certificates",
      color: "#2c9faf",
      serviceType: 1
    },
    { 
      name: "Catalog", 
      icon: <FaBook size={40} />, 
      link: `/Course`,
      description: "Browse course catalog and details",
      color: "#4e73df",
      serviceType: 2
    },
    { 
      name: "Schedule", 
      icon: <FaChalkboardTeacher size={40} />, 
      link: `/section`,
      description: "View class sections and schedules",
      color: "#1cc88a",
      serviceType: 3
    },
    { 
      name: "Faculty & Staff", 
      icon: <FaUserTie size={40} />, 
      link: `/FacultyAndStaff`,
      description: "Meet our faculty and staff members",
      color: "#36b9cc",
      serviceType: 4
    },
    { 
      name: "Office Hours", 
      icon: <FaClock size={40} />, 
      link: `/FacultyOfficeHours`,
      description: "Find faculty office hours and contact info",
      color: "#5a5c69",
      serviceType: 5
    },
    { 
      name: "Class Schedule", 
      icon: <FaCalendarAlt size={40} />, 
      link: `/FacultyClassSchedule`,
      description: "View faculty teaching schedules",
      color: "#e74a3b",
      serviceType: 6
    },
    { 
      name: "Syllabus Repository", 
      icon: <FaFileAlt size={40} />, 
      link: `/Syllabus`,
      description: "Access course syllabi and materials",
      color: "#f6c23e",
      serviceType: 7
    },
    { 
      name: "Final Exam Schedule", 
      icon: <FaCalendarCheck size={40} />, 
      link: `/FinalExamSchedule`,
      description: "View final examination schedules",
      color: "#8b44ff",
      serviceType: 8
    }
  ];

  useEffect(() => {
      fetchServiceData(deptId);
  }, []);


  const fetchServiceData = () => {
    setIsServicesLoading(true);
    DepartmentService.GetService()
      .then(response => {
        if (response.data && response.data.length > 0) {
          const serviceItems = response.data.map(service => {
            // Find the matching icon based on serviceType
            const iconMap = {
              1: <FaGraduationCap size={40} />,
              2: <FaBook size={40} />,
              3: <FaChalkboardTeacher size={40} />,
              4: <FaUserTie size={40} />,
              5: <FaClock size={40} />,
              6: <FaCalendarAlt size={40} />,
              7: <FaFileAlt size={40} />,
              8: <FaCalendarCheck size={40} />
            };
            
            // Find the matching color based on serviceType
            const colorMap = {
              1: "#2c9faf",
              2: "#4e73df",
              3: "#1cc88a",
              4: "#36b9cc",
              5: "#5a5c69",
              6: "#e74a3b",
              7: "#f6c23e",
              8: "#8b44ff"
            };
            
            return {
              name: service.serviceTitle,
              icon: iconMap[service.serviceType] || <FaUniversity size={40} />,
              link: `${service.link}`,
              description: service.serviceSubTitle,
              color: colorMap[service.serviceType] || "#4e73df",
              serviceType: service.serviceType
            };
          });
          
          setNavItems(serviceItems);
        } else {
          // If API returns empty array, use default navItems
          setNavItems(defaultNavItems);
        }
        setIsServicesLoading(false);
      })
      .catch(error => {
        console.error('Error fetching service data:', error);
        // On error, use default navItems
        setNavItems(defaultNavItems);
        setIsServicesLoading(false);
      });
  };

  const handleDepartmentDetails = (data) => {
    setDepartmentDetails(data);
  };

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const pageTransition = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    },
    exit: { 
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.4
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      y: 10, 
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "tween",
        duration: 0.3
      }
    },
    hover: {
      y: -5,
      transition: {
        duration: 0.2
      }
    }
  };

  const iconVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  const arrowVariants = {
    hover: {
      x: 3,
      transition: {
        duration: 0.2
      }
    }
  };

  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 0.5,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageTransition}
      className="container-fluid p-0" 
      style={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        minHeight: 'calc(100vh - 368px)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Background Decorative Elements */}
      <motion.div
        variants={backgroundVariants}
        initial="hidden"
        animate="visible"
        style={{
          position: 'absolute',
          top: '5%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(74, 222, 128, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
          filter: 'blur(40px)',
          zIndex: 0
        }}
      />
      <motion.div
        variants={backgroundVariants}
        initial="hidden"
        animate="visible"
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(236, 72, 153, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)',
          filter: 'blur(40px)',
          zIndex: 0
        }}
      />

      <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
      {/* <DepartmentBanner /> */}

        <div className="container-custom p-5" style={{ 
          //paddingTop: '0.5rem',
          paddingBottom: '1rem',
          width: '100%'
        }}>
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-3 d-none">
            
          </nav>

          {/* Navigation Cards */}
          {!isServicesLoading && (
            <motion.div 
              ref={ref}
              className="row g-4 justify-content-center"
              variants={containerVariants}
              initial="hidden"
              animate={controls}
            >
              {navItems.map((item, index) => (
                <motion.div 
                  key={index} 
                  className="col-md-6 col-lg-3"
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <Link 
                    to={item.link} 
                    className="text-decoration-none"
                    style={{ color: 'inherit' }}
                  >
                    <motion.div 
                      className={`card h-100 border-0 ${styles.navCard}`}
                      style={{
                        borderRadius: "8px",
                        background: "white",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s ease"
                      }}
                    >
                      <div className="card-body d-flex flex-column align-items-center text-center p-4">
                        <motion.div 
                          className="rounded-circle d-flex align-items-center justify-content-center mb-4" 
                          style={{ 
                            width: "70px", 
                            height: "70px", 
                            backgroundColor: `${item.color}15`,
                            color: item.color
                          }}
                          variants={iconVariants}
                        >
                          {item.icon}
                        </motion.div>
                        <h4 className="mb-2" style={{ fontWeight: "600", color: "#333" }}>{item.name}</h4>
                        <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>{item.description}</p>
                        <motion.div 
                          className="mt-auto"
                        >
                          <motion.span 
                            className="btn btn-sm btn-outline-primary"
                            style={{
                              borderRadius: "4px",
                              padding: "6px 12px",
                              fontSize: "0.85rem"
                            }}
                            variants={arrowVariants}
                            whileHover="hover"
                          >
                            Explore <FaArrowRight className="ms-1" size={10} />
                          </motion.span>
                        </motion.div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
          
          {isServicesLoading && (
            <div className="d-flex justify-content-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {/* Quick Stats Section */}
          {/* <div className="row mt-5 g-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Academic Programs</h6>
                  <h3 className="mb-0">6</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Faculty Members</h6>
                  <h3 className="mb-0">25+</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Student Resources</h6>
                  <h3 className="mb-0">10+</h3>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </motion.div>
  );
};

export default Home;
